/**
 * RP+TP+FC 血缘组合转出 — §8.1
 */
import { isLevelGte } from './levels.js';

export type LineageTransferDirection = 'up' | 'down';

/** 上转 → 上级（空间站）；FC/RP 组合上转接收方 ≥ A3 */
export const LINEAGE_UP_MIN_LEVEL = 'A3';

/** TP 链上充值最低级别（服务级 reward_level） */
export const LINEAGE_TP_RECHARGE_MIN_LEVEL = 'A3';

/** TP 血缘转出免手续费最低级别 */
export const LINEAGE_TP_FEE_FREE_MIN_LEVEL = 'A5';

/** A3～A4 转出 TP 时收取手续费 */
export const LINEAGE_TP_FEE_APPLY_FROM_LEVEL = 'A3';

/** uo_config key — A3～A4 转出 TP 手续费百分比（如 10 = 10%） */
export const LINEAGE_TP_FEE_CONFIG_KEY = 'lineage.tp_fee_pct_a3';

export const LINEAGE_TP_FEE_PCT_DEFAULT = '10';

export const LINEAGE_TRANSFER_MIN_UP = {
  tp: '10',
  rp: '10',
  fc: '0.05',
} as const;

/** 下转 → 推荐链下级（可隔无限代） */
export const LINEAGE_TRANSFER_MIN_DOWN = {
  tp: '10',
  rp: '1',
  fc: '0.05',
} as const;

export const LINEAGE_TRANSFER_ASSETS = ['rp', 'tp', 'fc'] as const;
export type LineageTransferAsset = (typeof LINEAGE_TRANSFER_ASSETS)[number];

export function lineageMinForDirection(
  direction: LineageTransferDirection,
): Record<LineageTransferAsset, string> {
  return direction === 'up' ? LINEAGE_TRANSFER_MIN_UP : LINEAGE_TRANSFER_MIN_DOWN;
}

export function recipientQualifiesForUp(levelCode: string): boolean {
  return isLevelGte(levelCode, LINEAGE_UP_MIN_LEVEL);
}

/** RP 仅可下转；TP/FC 上下转均可（推荐链祖先/后代，可隔无限代） */
export function assetAllowsDirection(
  asset: LineageTransferAsset,
  direction: LineageTransferDirection,
): boolean {
  if (asset === 'rp' && direction === 'up') return false;
  return true;
}

export function qualifiesForTpRecharge(levelCode: string): boolean {
  return isLevelGte(levelCode, LINEAGE_TP_RECHARGE_MIN_LEVEL);
}

/** TP recharge gate uses 服务级 (reward_level), not 投资星级 (user_level). */
export function qualifiesForTpRechargeProfile(input: {
  level_code?: string | null;
  reward_level_code?: string | null;
}): boolean {
  const reward = input.reward_level_code?.trim();
  if (reward) return qualifiesForTpRecharge(reward);
  const userLevel = input.level_code?.trim();
  return userLevel ? qualifiesForTpRecharge(userLevel) : false;
}

/** Fee percent string for TP lineage transfer (0 when A5+ or below A3). */
export function tpLineageFeePct(senderLevelCode: string, configuredPct: string): string {
  if (isLevelGte(senderLevelCode, LINEAGE_TP_FEE_FREE_MIN_LEVEL)) return '0';
  if (isLevelGte(senderLevelCode, LINEAGE_TP_FEE_APPLY_FROM_LEVEL)) return configuredPct;
  return '0';
}

/** Legacy single-asset 转出 step (multiples). */
export function lineageStepForSingle(
  asset: LineageTransferAsset,
  direction: LineageTransferDirection,
): string {
  if (asset === 'tp') return '10';
  if (asset === 'fc') return '0.05';
  return direction === 'up' ? '10' : '1';
}
